/**
 * Roles, access levels, and the shape of a user row.
 *
 * The same two mappings were written out three times — the display map in
 * src/pages/UserManagement.js, its inverse in that page's submit handler, and
 * ACCESS_LEVEL_ROLES in the redesigned sidebar. Three copies of a numeric
 * mapping is three chances for "13" to mean two different jobs, and nothing
 * would fail loudly if it did: a wrong role renders as a plausible label.
 *
 * Kept free of axios and React so it can be unit tested.
 */

/** Ergo Baltic's access levels. The numbers are the backend's. */
export const ACCESS_LEVEL_ROLES = Object.freeze({
  10: "Admin",
  11: "EE Motor Sales",
  12: "EE Motor UW",
  13: "EE Property Sales",
  14: "EE Property UW",
  15: "LT Motor Sales",
  16: "LT Motor UW",
  17: "LT Property Sales",
  18: "LT Property UW",
  19: "LV Motor Sales",
  20: "LV Motor UW",
  21: "LV Property Sales",
  22: "LV Property UW",
});

/** Every role, in access-level order — which is Admin first, then by country. */
export const ROLES = Object.freeze(Object.values(ACCESS_LEVEL_ROLES));

const ROLE_TO_LEVEL = Object.freeze(
  Object.entries(ACCESS_LEVEL_ROLES).reduce((out, [level, role]) => {
    out[role] = level;
    return out;
  }, {}),
);

/** A dash rather than a guess: an unmapped level is a backend change. */
export const roleForAccessLevel = (level) =>
  ACCESS_LEVEL_ROLES[String(level)] || null;

/**
 * The access level to send for a role.
 *
 * Falls back to Admin only because the existing page does; that default is
 * generous, so callers should pass a role that exists.
 */
export const accessLevelForRole = (role) => ROLE_TO_LEVEL[role] || "10";

/** "reviewer" -> "Reviewer". Left alone if it's not a plain lowercase word
 * (Ergo's roles already arrive Title Case via roleForAccessLevel). */
const titleCase = (value) =>
  /^[a-z][a-z ]*$/.test(value) ? value.replace(/\b\w/g, (c) => c.toUpperCase()) : value;

/**
 * One API user as a row.
 *
 * Three backends, three field vocabularies: Ergo answers full_name / user_id
 * / access_level, Bajaj answers username / email_id / role, Hegic answers
 * name / email / role / permission (role lowercase — "reviewer" — title-cased
 * for display; permission a comma string or array of codes, decoded by
 * permissionLabelsFor against the logged-in client's own list since the
 * codes mean nothing without it). All handled here rather than at each call
 * site. Hegic's exact /get_user field names are unconfirmed (no captured
 * response to check against) — these are the names its /add_user accepts,
 * assumed symmetric; update this if a real response disagrees.
 *
 * `flag` is the active switch and arrives as a number from one host and a
 * string from the other — Ergo and Bajaj both use 1 for active (2 or
 * anything else is inactive), which is the default here. Hegic's own
 * suspend/activate action (confirmed via a captured `PUT /edit_user/{id}/`
 * fired from clicking "Activate" on a suspended account) sends `flag: 0` to
 * re-activate — the opposite convention — so its own entry in hegic.json
 * (`activeFlagValue: 0`) overrides the default instead of this guessing per
 * backend. A caller that doesn't pass `activeFlagValue` keeps Ergo/Bajaj's
 * behaviour exactly as it was.
 */
export const toUserRow = (user, index = 0, { activeFlagValue = 1 } = {}) => {
  const source = user || {};
  const role = source.role || roleForAccessLevel(source.access_level);
  return {
    id: String(source.id ?? index + 1),
    name: source.username || source.full_name || source.name || "Unknown",
    email: source.email_id || source.user_id || source.email || "",
    role: role ? titleCase(role) : null,
    permission: source.permission ?? null,
    accessLevel:
      source.access_level === undefined || source.access_level === null
        ? null
        : String(source.access_level),
    lastModified: source.date_updated || null,
    active:
      source.flag !== undefined &&
      source.flag !== null &&
      String(source.flag) === String(activeFlagValue),
    // Ergo motor's edit route wants a field (`anc_access_level`) this app has
    // no UI for and no idea what it means — carrying the untouched original
    // response through lets a write path round-trip a value it must not
    // guess at or blank, without every other caller needing to know it exists.
    _raw: source,
  };
};

/**
 * The API answers with an object keyed by id as often as with an array.
 * `options` (currently just `activeFlagValue`) passes straight through to
 * every row — see toUserRow.
 */
export const toUserRows = (data, options) =>
  (Array.isArray(data) ? data : Object.values(data || {})).map((user, index) =>
    toUserRow(user, index, options),
  );

/**
 * A raw `permission` value ("1,3" or ["1","3"]) as the labels the logged-in
 * client's own config names them — the codes have no meaning on their own
 * (confirmed nowhere in any API doc), only against the list the Add User
 * form itself offered when they were set.
 */
export const permissionLabelsFor = (rawPermission, permissionOptions = []) => {
  if (!rawPermission) return [];
  const codes = Array.isArray(rawPermission)
    ? rawPermission
    : String(rawPermission).split(",");
  const labelByCode = new Map(
    permissionOptions.map((option) => [String(option.code), option.label]),
  );
  return codes
    .map((code) => labelByCode.get(String(code).trim()))
    .filter(Boolean);
};

/** Initials for an avatar, from whatever name the backend gave. */
export const initialsOf = (name) => {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};
